import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Mail, Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { safeReturnPath } from "@/lib/auth/returnPath";

export default function LoginPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = safeReturnPath((location.state as { from?: unknown } | null)?.from);

  useEffect(() => {
    if (user) navigate(returnTo, { replace: true });
  }, [user, navigate, returnTo]);

  const [loading, setLoading] = useState(false);

  if (user) return null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-primary text-primary-foreground font-logo font-bold text-2xl">
            C
          </div>
          <h1 className="mt-4 font-logo text-3xl font-bold text-foreground">Tristano's Catering</h1>
          <p className="mt-1 text-sm font-secondary text-muted-foreground">Staff Portal</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Sign In</CardTitle>
            <CardDescription>Enter your credentials to access the dashboard.</CardDescription>
          </CardHeader>
          <CardContent>
            <LoginForm loading={loading} setLoading={setLoading} />
          </CardContent>
        </Card>

        <MagicLinkSection loading={loading} setLoading={setLoading} />
      </div>
    </div>
  );
}

function LoginForm({ loading, setLoading }: { loading: boolean; setLoading: (v: boolean) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showReset, setShowReset] = useState(false);
  // The address whose sign-in failed on "email not confirmed", so a later edit to
  // the email box can't redirect the resend somewhere else. null = no such failure.
  const [unconfirmedEmail, setUnconfirmedEmail] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setUnconfirmedEmail(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      // The password grant validates the password before the confirmation check,
      // so this only fires for someone who already has the right credentials —
      // it isn't an account-enumeration oracle. Matched on both code and message:
      // code is the documented discriminator, the regex is a fallback in case the
      // deployed GoTrue populates only message (#732).
      if (error.code === "email_not_confirmed" || /email not confirmed/i.test(error.message)) {
        setUnconfirmedEmail(email);
      }
      toast.error(error.message);
    } else {
      toast.success("Signed in successfully");
    }
  };

  const handleResendVerification = async () => {
    if (!unconfirmedEmail) return;
    setResending(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: unconfirmedEmail,
      // Not /reset-password: a type=signup callback isn't one ResetPasswordPage
      // handles, so it would render "Invalid Link". Landing on /admin lets
      // supabase-js consume the hash and establish the session instead.
      options: { emailRedirectTo: `${window.location.origin}/admin` },
    });
    setResending(false);
    if (error) toast.error(error.message);
    else toast.success("Verification email sent — check your inbox.");
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your email first"); return; }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (error) toast.error(error.message);
    else toast.success("Password reset email sent!");
    setShowReset(false);
  };

  if (showReset) {
    return (
      <form onSubmit={handleResetPassword} className="space-y-4">
        <CardDescription>Enter your email to receive a password reset link.</CardDescription>
        <div className="space-y-2">
          <Label htmlFor="reset-email">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input id="reset-email" type="email" placeholder="you@example.com" value={email}
              onChange={(e) => { setEmail(e.target.value); setUnconfirmedEmail(null); }}
              className="pl-9" required />
          </div>
        </div>
        <Button type="submit" className="w-full" disabled={loading}>
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send Reset Link
        </Button>
        <Button type="button" variant="ghost" className="w-full" onClick={() => setShowReset(false)}>
          Back to sign in
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleLogin} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="login-email">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input id="login-email" type="email" placeholder="you@example.com" value={email}
            onChange={(e) => { setEmail(e.target.value); setUnconfirmedEmail(null); }}
            className="pl-9" required />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="login-password">Password</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input id="login-password" type="password" placeholder="••••••••" value={password}
            onChange={(e) => setPassword(e.target.value)} className="pl-9" required />
        </div>
      </div>
      <Button type="submit" className="w-full" disabled={loading}>
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign In
      </Button>
      {unconfirmedEmail && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 space-y-2">
          <p className="text-sm text-foreground">
            {unconfirmedEmail} hasn't been verified yet, so we can't sign you in.
          </p>
          <Button type="button" variant="secondary" className="w-full" disabled={resending}
            onClick={handleResendVerification}>
            {resending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Resend verification email
          </Button>
        </div>
      )}
      <button type="button" onClick={() => setShowReset(true)}
        className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors">
        Forgot password?
      </button>
    </form>
  );
}

function MagicLinkSection({ loading, setLoading }: { loading: boolean; setLoading: (v: boolean) => void }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/admin`,
        shouldCreateUser: false,
      },
    });
    setLoading(false);
    if (error) toast.error(error.message);
    else { setSent(true); toast.success("Magic link sent! Check your email."); }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-sans font-medium">Passwordless Login</CardTitle>
        <CardDescription>We'll email you a magic link to sign in.</CardDescription>
      </CardHeader>
      <CardContent>
        {sent ? (
          <p className="text-sm text-muted-foreground text-center py-2">
            ✉️ Check your inbox for the magic link!
          </p>
        ) : (
          <form onSubmit={handleMagicLink} className="flex gap-2">
            <Input type="email" placeholder="you@example.com" value={email}
              onChange={(e) => setEmail(e.target.value)} required className="flex-1" />
            <Button type="submit" variant="secondary" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Link"}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
